import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { FocusOn } from 'react-focus-on';
import { CSSTransition } from 'react-transition-group';
import styles from './styles/modal.module.css';
import { ModalContext, ScrollContext } from './modal-context';
import { ModalCloseButton } from './modal-close-button';
import { useCustomBreakpoints } from '../hooks';
import { BREAKPOINTS } from './constants';
import { FocusOnContext, useFocusOnIgnore } from '../_internal/hooks';
import { useModalRefs } from './use-modal-refs';
/**
 * `<Modal>` - Предоставляет логику и анимацию всплывающего окна.
 *
 * @example ``` tsx
 <Modal onClose={() => close()} show>
 Content
 </Modal>;
 * ```
 */
export const Modal = /*#__PURE__*/forwardRef(function Modal({
  children,
  modalID,
  className = '',
  onClose,
  size = 'm',
  variant = 'simple',
  show,
  toTop = false,
  sticky,
  overlayClose = true,
  closeButton = true,
  overlayType = 'dark'
}, ref) {
  const elementRole = 'modal';
  const [firstModalHeight, setFirstModalHeight] = useState(null);
  const [modalPosition, setModalPosition] = useState(0);
  const [modalTitleType, setModalTitleType] = useState('title');
  const [scrollTop, setScrollTop] = useState(false);
  const [scrollBottom, setScrollBottom] = useState(false);
  const {
    modalRef,
    overlayRef,
    nodeRef
  } = useModalRefs();
  const {
    documentMouseDown
  } = useFocusOnIgnore();
  const overLayClass = useMemo(() => clsx(styles.overlay, styles[variant], styles[`overlay-type-${overlayType}`]), [variant, overlayType]);
  const modalClass = useMemo(() => clsx(styles.modal, styles[variant], styles[size], sticky ? styles.sticky : '', className), [size, variant, sticky, className]);
  const wrapperClass = useMemo(() => clsx(styles.wrapper, styles[size], styles[variant], toTop ? styles.toTop : ''), [size, variant, toTop]);
  const breakpoint = useCustomBreakpoints(BREAKPOINTS);
  const timerCurtain = useMemo(() => breakpoint === 'mobile' ? {
    enter: 200,
    exit: 400
  } : {
    enter: 150,
    exit: 350
  }, [breakpoint]);
  const timeout = useMemo(() => {
    switch (variant) {
      case 'full-screen':
        return {
          enter: 100,
          exit: 150
        };
      case 'curtain':
        return timerCurtain;
      default:
        return {
          enter: 150,
          exit: 350
        };
    }
  }, [variant, timerCurtain]);
  const modalStyles = useMemo(() => {
    let stylesObj = {
      '--modalPosition': modalPosition
    };
    if (firstModalHeight && modalPosition !== 0) {
      stylesObj = {
        ...stylesObj,
        '--firstModalHeight': `${firstModalHeight}px`
      };
    }
    if (modalTitleType) {
      const gap = modalTitleType === 'title' ? 48 : 40;
      stylesObj = {
        ...stylesObj,
        '--gapBetweenModals': `${gap}px`
      };
    }
    return stylesObj;
  }, [modalPosition, firstModalHeight, modalTitleType]);
  useEffect(() => {
    if (show) {
      const modals = document.querySelectorAll(`[data-element-role="${elementRole}"]`);
      if (modals && modals.length && modalRef && modalRef.current) {
        const modalsArray = Array.from(modals);
        const index = modalsArray.indexOf(modalRef.current);
        const first = modalsArray[0];
        setModalPosition(index);
        if (first) {
          setFirstModalHeight(first.getBoundingClientRect().height);
        }
      } else {
        setModalPosition(0);
      }
    }
  }, [show, children, modalRef]);
  const changeTitleType = useCallback(value => {
    setModalTitleType(value);
  }, []);
  const changeContentOnScroll = useCallback(value => {
    setScrollTop(value.top);
    setScrollBottom(value.bottom);
  }, []);
  const clickOutside = useCallback(event => {
    var _modalRef$current, _overlayRef$current;
    if (show && !((_modalRef$current = modalRef.current) != null && _modalRef$current.contains(event.target)) && overlayRef && (_overlayRef$current = overlayRef.current) != null && _overlayRef$current.contains(event.target)) {
      onClose();
    }
  }, [show, modalRef, overlayRef, onClose]);
  useEffect(() => {
    if (overlayClose && show) {
      document.addEventListener('click', clickOutside);
    }
    if (show) {
      document.addEventListener('mousedown', documentMouseDown);
    }
    return () => {
      document.removeEventListener('click', clickOutside);
      document.removeEventListener('mousedown', documentMouseDown);
    };
  }, [overlayClose, show, clickOutside, documentMouseDown]);
  return /*#__PURE__*/React.createElement(CSSTransition, {
    nodeRef: nodeRef,
    timeout: timeout,
    in: show,
    classNames: {
      enter: styles.modalTransitionEnter,
      enterActive: styles.modalTransitionEnterActive,
      exit: styles.modalTransitionExit,
      exitActive: styles.modalTransitionExitActive
    },
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.animationWrapper,
    ref: nodeRef
  }, /*#__PURE__*/React.createElement(FocusOn, {
    onClickOutside: onClose,
    autoFocus: false,
    onEscapeKey: onClose
  }, /*#__PURE__*/React.createElement("div", {
    ref: overlayRef,
    className: overLayClass,
    style: modalStyles,
    "data-testid": "modal-overlay"
  }, /*#__PURE__*/React.createElement("div", {
    className: wrapperClass,
    ref: ref
  }, /*#__PURE__*/React.createElement("div", {
    className: modalClass,
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": modalID && `${modalID}_label`,
    "aria-describedby": modalID && `${modalID}_desc`,
    "data-element-role": elementRole,
    ref: modalRef,
    "data-testid": "modal-window"
  }, closeButton && /*#__PURE__*/React.createElement(ModalCloseButton, {
    onClose: onClose
  }), /*#__PURE__*/React.createElement(ScrollContext.Provider, {
    value: {
      changeContentOnScroll,
      scrollTop,
      scrollBottom
    }
  }, /*#__PURE__*/React.createElement(ModalContext.Provider, {
    value: {
      variant,
      changeContextTitleType: changeTitleType,
      sticky,
      size
    }
  }, /*#__PURE__*/React.createElement(FocusOnContext.Provider, {
    value: true
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.contentWrapper
  }, children))))))))));
});